// src/review/ReviewMode.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Stack, Loader, Center, Alert, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { ReviewProvider } from './context/ReviewContext';
import { startReview } from '../redux/slices/reviewSlice';
import RiskReviewStep from './steps/RiskReviewStep';
import SecurityReviewStep from './steps/SecurityReviewStep';
import ReviewSummary from './steps/ReviewSummary';

/**
 * ReviewMode - Main orchestrator for risk manager review flow
 *
 * Flow varies by assessmentType:
 *   - builder: Risks → Security → Summary
 *   - navigator/explorer: Risks → Summary (security is reviewed on the parent Builder)
 *
 * @param {Object} props
 * @param {string} props.modelId - Model UUID
 * @param {string} props.assessmentUuid - Assessment UUID
 * @param {string} [props.assessmentType='builder'] - 'builder' | 'navigator' | 'explorer'
 */
function ReviewMode({ modelId, assessmentUuid, assessmentType = 'builder' }) {
  const dispatch = useDispatch();

  // Get review state from RTK reviewSlice (assessment-scoped)
  const reviewState = useSelector((state) =>
    state.review?.byAssessment?.[assessmentUuid] || {}
  );

  const loading = useSelector((state) => state.review?.loading);
  const error = useSelector((state) => state.review?.error);

  const currentStep = reviewState.currentStep || 'risks';

  // Debug logging
  console.log('[ReviewMode] Render state:', {
    assessmentUuid,
    modelId,
    reviewState,
    loading,
    error,
    currentStep,
    hasReviewStatus: !!reviewState.reviewStatus
  });

  // Initialize review session on mount ONLY if not already started
  // Note: AssessmentCanvasV2 calls startReview before rendering ReviewMode,
  // so we only need to initialize if reviewState is empty (standalone usage)
  useEffect(() => {
    const initializeReview = async () => {
      try {
        await dispatch(startReview({ modelId, assessmentUuid }));
      } catch (err) {
        console.error('[ReviewMode] Failed to start review:', err);
      }
    };

    // Only start review if not already started (check for existing reviewStatus)
    if (modelId && assessmentUuid && !reviewState.reviewStatus) {
      initializeReview();
    }
  }, [modelId, assessmentUuid, dispatch, reviewState.reviewStatus]);

  // Validation
  if (!modelId || !assessmentUuid) {
    return (
      <Container fluid px="xl" py="xl">
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Missing required parameters (modelId or assessmentUuid). Cannot load review mode.
        </Alert>
      </Container>
    );
  }

  // Loading state
  if (loading && !reviewState.reviewStatus) {
    return (
      <Container fluid px="xl" py="xl">
        <Center style={{ minHeight: '400px' }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="lg" c="dimmed">Initializing review session...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container fluid px="xl" py="xl">
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Review Error">
          {error}
        </Alert>
      </Container>
    );
  }

  // Builder/Explorer reviews don't include their own security step —
  // security is owned by the parent Builder review. Coerce to summary if
  // somehow the step is set to 'security'.
  const skipSecurity = assessmentType === 'navigator' || assessmentType === 'explorer';
  const effectiveStep = skipSecurity && currentStep === 'security' ? 'summary' : currentStep;

  // Render current step (dynamic, not hard-coded)
  const renderStep = () => {
    switch (effectiveStep) {
      case 'risks':
        return <RiskReviewStep />;
      case 'security':
        return <SecurityReviewStep />;
      case 'summary':
        return <ReviewSummary />;
      default:
        return (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            Unknown review step: {effectiveStep}
          </Alert>
        );
    }
  };

  return (
    <ReviewProvider modelId={modelId} assessmentUuid={assessmentUuid} assessmentType={assessmentType}>
      <Container fluid px="xl" py="xl">
        {renderStep()}
      </Container>
    </ReviewProvider>
  );
}

ReviewMode.propTypes = {
  modelId: PropTypes.string.isRequired,
  assessmentUuid: PropTypes.string.isRequired,
  assessmentType: PropTypes.oneOf(['builder', 'navigator', 'explorer'])
};

export default ReviewMode;
